import React, { useEffect, useMemo, useState } from 'react';
import { Descriptions, message, Modal, Badge, Empty, Button } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import InterviewSlotViewModal from './interview-slot-view-modal';
import jobsService from '../../services/jobs.service';
import { notifyError, notifySuccess } from '../../utils/notification';

const InterviewSlotElement = styled(Descriptions)`
	background-color: rgba(0, 0, 0, 0.1);
	padding: 10px;
	border-radius: 5px;
`;
const statusMap = {
	SCHEDULED: 'processing',
	CANCELED: 'error',
	FREE: 'success',
	MISSED: 'warning'
};

const SlotStatus = {
	FREE: 'FREE',
	SCHEDULED: 'SCHEDULED',
	CANCELED: 'CANCELED',
	ATTENDED: 'ATTENDED',
	MISSED: 'MISSED'
};

const InterviewSlotsViewModal = ({
	selectedJob,
	interviewSlots,
	visible,
	onCancel,
	onSloteCreate,
	backOffice,
	jobs,
	onEditSlotSuccess,
	onEditSlotError,
	onSchedule
}) => {
	const [selectedSlot, setSelectedSlot] = useState<any>(null);
	const [candidates, setCandidates] = useState({});

	const cancelSlotHandler = () => {
		notifySuccess('Slot is canceled successfully!');
		const cancelledSlot = { ...selectedSlot, status: 'CANCELED' };
		interviewSlots[interviewSlots.findIndex(slot => slot.id === selectedSlot.id)] = cancelledSlot;
		setSelectedSlot(null);
	};

	const selectSlotHandler = async slot => {
		setSelectedSlot(slot);
		let assignableCandidates;
		if (backOffice && !candidates[selectedJob._id]) {
			assignableCandidates = await jobsService.getInterviewAssignableCandidates(selectedJob._id);
		}
		setCandidates(assignableCandidates ? { ...candidates, [selectedJob._id]: assignableCandidates } : candidates);
	};
	const scheduleSuccessHandler = assignedCandidate => {
		onSchedule && onSchedule(assignedCandidate);
		const candidate = candidates[selectedJob._id].find(candidate => candidate.id === assignedCandidate);

		notifySuccess(
			`Candidate "${candidate.firstName} ${candidate.lastName}" is successfully assigned for an interview to job ${selectedJob.title} for ${selectedSlot.company.name} company`
		);
		const slots = interviewSlots.map(slot => {
			if (slot.id === selectedSlot.id) {
				slot.status = SlotStatus.SCHEDULED;
				slot.candidate = candidate;
			}
			return slot;
		});

		interviewSlots = slots;
		setSelectedSlot(null);
	};

	return (
		<>
			<Modal
				className="ta-modal"
				visible={visible}
				title={`Interview slots for ${selectedJob?.title} job`}
				onCancel={onCancel}
				onOk={onCancel}
				bodyStyle={{
					overflow: 'auto',
					maxHeight: '78vh'
				}}
			>
				{interviewSlots?.length ? (
					interviewSlots.map(slot => (
						<div key={slot.id} style={{ cursor: 'pointer' }} onClick={() => selectSlotHandler(slot)}>
							<InterviewSlotElement
								layout="horizontal"
								column={{ xs: 1, md: 1 }}
								className="mb-2"
								size="small"
							>
								<Descriptions.Item label="Status">
									<Badge status={statusMap[slot.status]} text={slot.status} />
								</Descriptions.Item>

								<Descriptions.Item label="Date">
									{dayjs(slot.start).format('DD/MM/YYYY')}
								</Descriptions.Item>

								<Descriptions.Item label="Time">
									{`${dayjs(slot.start).format('HH:mm a')} - ${dayjs(slot.end).format('HH:mm a')}`}
								</Descriptions.Item>

								<Descriptions.Item label="Interviewer Name">{slot.interviewer.name}</Descriptions.Item>

								<Descriptions.Item label="Interviewer Email">
									{slot.interviewer.email}
								</Descriptions.Item>
							</InterviewSlotElement>
						</div>
					))
				) : backOffice ? (
					<Empty description="No interview slots!" />
				) : (
					<Empty description="No interview slots!">
						<Button type="primary" onClick={onSloteCreate}>
							Create Now
						</Button>
					</Empty>
				)}
			</Modal>
			{selectedSlot ? (
				<InterviewSlotViewModal
					visible={selectedSlot}
					job={selectedJob}
					jobs={jobs}
					slot={selectedSlot}
					onCancel={() => setSelectedSlot(null)}
					onSuccess={scheduleSuccessHandler}
					onError={e => notifyError(e.errorCode.toLowerCase().split('_').join(' '))}
					onSlotCancelSuccess={cancelSlotHandler}
					backOffice={backOffice}
					onEditSlotSuccess={slot => {
						onEditSlotSuccess(slot);
						setSelectedSlot({
							...selectedSlot,
							start: slot.appointment.from,
							end: slot.appointment.to,
							interviewer: slot.interviewer,
							location: slot.location,
							isOnline: slot.isOnline,
							notes: slot.notes
						});
					}}
					onEditSlotError={onEditSlotError}
					candidates={candidates[selectedJob._id]}
				/>
			) : null}
		</>
	);
};

export default InterviewSlotsViewModal;
